package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.example.mystybag.appCore.logic.pages.SettingsPageViewModel
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.core.defines.UtilsWalletDefines.THEME_CODE_DARK
import com.example.mystybag.core.defines.UtilsWalletDefines.THEME_CODE_LIGHT
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.ui.views.UIView
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.width
import com.example.mystybag.core.ui.theme.LocalTheme.p14
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import dev.icerock.moko.resources.compose.painterResource

@Composable
fun SettingsPageView(viewModel: SettingsPageViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current
    val uiState = viewModel.uiState.value
    // Wide desktop layout only on landscape Web; portrait Web + native use the mobile layout.
    val isWeb = rememberIsWebDesktopLayout()

    UIView(
        modifier = if (isWeb) {
            Modifier.fillMaxSize()
        } else {
            Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .scrollEdgeFade()
        },
        viewModel = viewModel,
        showTopBar = !isWeb,
        removeVerticalPadding = false,
        navigationContent = if (!isWeb) {
            {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = p16),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .size(LocalTheme.p48)
                            .clip(CircleShape)
                            .background(AppTheme.current.colors.surfaceContainerLowest)
                            .noRippleClickable {
                                keyboardController?.hide()
                                viewModel.navCallbacks.backButtonCallback()
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            painter = painterResource(SRC.images.arrow_back),
                            contentDescription = null,
                            contentScale = ContentScale.Fit,
                            modifier = Modifier.size(width = 10.dp, height = 18.dp),
                            colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
                        )
                    }

                    Texts.TitleMedium(
                        text = sharedString(SR.strings.settings),
                        modifier = Modifier.weight(1f),
                        textAlign = TextAlign.Center,
                        color = AppTheme.current.colors.onSurface
                    )

                    Spacer(modifier = Modifier.size(LocalTheme.p48))
                }
            }
        } else null
    ) {
        if (isWeb) {
            Column(modifier = Modifier.fillMaxSize()) {
                ClientWebHeaderBar(
                    selectedTab = 3,
                    homeTabIndex = 0,
                    deliveryTabIndex = 1,
                    favoritesTabIndex = 2,
                    menuTabIndex = 3,
                    searchCallbacks = null,
                    onFilterClick = {},
                    showSearchAndFilter = false,
                    showBackButton = true,
                    onBackClick = viewModel::onBackClick,
                    centerTitle = sharedString(SR.strings.settings)
                )
                WebHeaderBottomDivider()
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.TopCenter
                ) {
                    key(uiState.currentLanguageCode) {
                        SettingsPageContent(
                            uiState = uiState,
                            viewModel = viewModel,
                            modifier = Modifier
                                .width(1080.dp)
                                .verticalScroll(rememberScrollState())
                                .padding(horizontal = p16, vertical = p32)
                        )
                    }
                }
            }
        } else {
            key(uiState.currentLanguageCode) {
                SettingsPageContent(
                    uiState = uiState,
                    viewModel = viewModel,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = p16, vertical = p32)
                )
            }
        }
    }
}

@Composable
private fun SettingsPageContent(
    uiState: com.example.mystybag.appCore.model.pages.SettingsPageUIState,
    viewModel: SettingsPageViewModel,
    modifier: Modifier
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(0.dp)
    ) {
        // Application language + native permissions: not shown on web (browser / single-locale UX).
        if (!PlatformHelper.isWeb) {
            // Section: APPLICATION SETTINGS
            SettingsSectionHeader(title = sharedString(SR.strings.section_application_settings))

            SettingsListItem(
                label = sharedString(SR.strings.language),
                value = uiState.languageDisplayValue,
                onClick = { viewModel.onLanguageClick() }
            )

            // Native permissions (system settings)
            // Section: ACCESS TO
            SettingsSectionHeader(
                title = sharedString(SR.strings.section_access_to),
                modifier = Modifier.padding(top = 24.dp)
            )

            SettingsListItem(
                label = sharedString(SR.strings.location),
                value = if (uiState.isLocationPermissionGranted) {
                    sharedString(SR.strings.allowed)
                } else {
                    sharedString(SR.strings.not_allowed)
                },
                onClick = { viewModel.onLocationPermissionClick() }
            )

            SettingsListItem(
                label = sharedString(SR.strings.notifications),
                value = if (uiState.isNotificationPermissionGranted) {
                    sharedString(SR.strings.allowed)
                } else {
                    sharedString(SR.strings.not_allowed)
                },
                onClick = { viewModel.onNotificationPermissionClick() }
            )
        }

        // Section: APPEARANCE
        SettingsSectionHeader(
            title = sharedString(SR.strings.section_appearance),
            modifier = Modifier.padding(top = if (PlatformHelper.isWeb) 0.dp else 24.dp)
        )

        // Theme selector: Light, Dark
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 24.dp, bottom = 24.dp),
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            ThemeOption(
                label = sharedString(SR.strings.theme_light),
                isSelected = uiState.selectedThemeCode == THEME_CODE_LIGHT,
                phoneImageRes = SRC.images.theme_phone_light,
                onClick = { viewModel.onThemeSelected(THEME_CODE_LIGHT) }
            )
            ThemeOption(
                label = sharedString(SR.strings.theme_dark),
                isSelected = uiState.selectedThemeCode == THEME_CODE_DARK,
                phoneImageRes = SRC.images.theme_phone_dark,
                onClick = { viewModel.onThemeSelected(THEME_CODE_DARK) }
            )
        }
    }
}

@Composable
private fun SettingsSectionHeader(
    title: String,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(p12)
    ) {
        Texts.LabelLarge(
            text = title.uppercase(),
            color = AppTheme.current.colors.onSurfaceVariant,
            modifier = Modifier
                .fillMaxWidth()
                .height(LocalTheme.p16)
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(p2)
                .background(AppTheme.current.colors.onSurfaceVariant.copy(alpha = 0.4f))
        )
    }
}

@Composable
private fun SettingsListItem(
    label: String,
    value: String,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(p48)
            .rippleClick(padding = p0, roundPadding = p8, action = onClick),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Texts.TitleMedium(
            text = label,
            color = AppTheme.current.colors.onSurface
        )
        Texts.BodyMedium(
            text = value.ifEmpty { "—" },
            color = AppTheme.current.colors.onSurfaceVariant,
            modifier = Modifier.weight(1f).padding(end = p12),
            textAlign = TextAlign.Right
        )
        Image(
            painter = painterResource(SRC.images.right_arrow),
            contentDescription = null,
            contentScale = ContentScale.Fit,
            modifier = Modifier.size(width = p8, height = p14),
            colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
        )
    }
}

@Composable
private fun ThemeOption(
    label: String,
    isSelected: Boolean,
    phoneImageRes: dev.icerock.moko.resources.ImageResource,
    onClick: () -> Unit
) {
    Column(
            modifier = Modifier
            .noRippleClickable(action = onClick),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Image(
            painter = painterResource(phoneImageRes),
            contentDescription = null,
            contentScale = ContentScale.Fit,
            modifier = Modifier.size(width = 72.dp, height = 127.dp)
        )
        Texts.TitleMedium(
            text = label,
            color = AppTheme.current.colors.onSurface,
            modifier = Modifier.height(24.dp)
        )
        Box(
            modifier = Modifier
                .size(24.dp)
                .clip(CircleShape)
                .then(
                    if (isSelected) Modifier
                        .background(AppTheme.current.colors.primary.copy(alpha = 0.2f))
                        .border(2.dp, AppTheme.current.colors.primary, CircleShape)
                    else Modifier
                        .border(2.dp, AppTheme.current.colors.onSurfaceVariant.copy(alpha = 0.6f), CircleShape)
                ),
            contentAlignment = Alignment.Center
        ) {
            if (isSelected) {
                Box(
                    modifier = Modifier
                        .size(14.dp)
                        .clip(CircleShape)
                        .background(AppTheme.current.colors.primary)
                )
            }
        }
    }
}
